import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, Search, User, Menu, X, Globe } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useCategories } from "../context/CategoryContext";
import { ProductSearch } from "./ProductSearch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const Header = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { groups, label } = useCategories();
  const { t, lang, toggle } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Telefonda başlıqda xanaya yer yoxdur (burger, loqo, dil, hesab, səbət),
  // ona görə axtarış ikonla açılır və başlığın altında tam enli sətir olur.
  const [searchOpen, setSearchOpen] = useState(false);

  // Səhifə dəyişəndə hər iki panel bağlanır.
  // Açıq menyu yeni səhifənin üstünü örtürdü: müştəri axtarış edirdi, nəticə
  // arxada açılırdı, ekranda isə hələ menyu dururdu. Hər keçid yolunu ayrıca
  // bağlamaq əvəzinə (loqo, kateqoriya, nəticə, «hamısına bax») marşrutun
  // özünə baxırıq — belədə heç bir yol unudulmur.
  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-brand-bg/80 border-b border-brand-line/60"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Left: mobile menu + nav */}
          <div className="flex items-center gap-8">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-brand-ink"
              onClick={() => { setMobileOpen((v) => !v); setSearchOpen(false); }}
            >
              {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
            </button>

            <nav className="hidden lg:flex items-center gap-8">
              {groups.map((g) => (
                <DropdownMenu key={g.key}>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`nav-${g.key}`} className="overline hover:text-brand-ink transition-colors">
                      {label(g.key, lang)}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-none border-brand-line">
                    {(g.children || []).map((c) => (
                      <DropdownMenuItem
                        key={c.key}
                        data-testid={`nav-cat-${c.key}`}
                        onClick={() => navigate(`/shop?category=${encodeURIComponent(c.key)}`)}
                        className="cursor-pointer text-sm tracking-wide"
                      >
                        {label(c.key, lang)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ))}

              <Link data-testid="nav-all" to="/shop" className="overline hover:text-brand-ink transition-colors">
                {t("nav_all")}
              </Link>
            </nav>
          </div>

          {/* Center: logo */}
          {/* Mütləq mərkəzləmə yalnız geniş ekranda — dar ekranda loqo
              normal axında qalır, yoxsa sağdakı ikonların üstünə düşür. */}
          <Link
            data-testid="logo-link"
            to="/"
            className="lg:absolute lg:left-1/2 lg:-translate-x-1/2"
          >
            <span className="font-serif text-lg tracking-[0.12em] sm:text-2xl sm:tracking-[0.2em] lg:text-3xl lg:tracking-[0.25em] text-brand-ink whitespace-nowrap">
              SPARKLE
            </span>
          </Link>

          {/* Right: actions */}
          <div className="flex items-center gap-5">
            <div className="hidden md:block">
              <ProductSearch />
            </div>

            <button
              data-testid="search-toggle"
              aria-label={t("search_placeholder")}
              onClick={() => { setSearchOpen((v) => !v); setMobileOpen(false); }}
              className="md:hidden text-brand-ink hover:text-brand-sub"
            >
              {searchOpen ? <X size={20} strokeWidth={1.5} /> : <Search size={20} strokeWidth={1.5} />}
            </button>

            <button data-testid="lang-toggle" onClick={toggle} className="flex items-center gap-1 overline hover:text-brand-ink">
              <Globe size={16} strokeWidth={1.5} />
              {lang.toUpperCase()}
            </button>

            <Link data-testid="account-link" to={user ? "/account" : "/login"} className="text-brand-ink hover:text-brand-sub">
              <User size={20} strokeWidth={1.5} />
            </Link>

            <button data-testid="cart-button" onClick={() => setOpen(true)} className="relative text-brand-ink hover:text-brand-sub">
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bg-brand-ink text-white text-[10px] w-4 h-4 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Telefonda axtarış sətri — ikonla açılır */}
      {searchOpen && (
        <div className="md:hidden border-t border-brand-line bg-brand-bg px-6 py-4">
          <ProductSearch inline autoFocus onDone={() => setSearchOpen(false)} />
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-brand-line bg-brand-bg px-6 py-6 space-y-5">
          {groups.map((g) => (
            <div key={g.key}>
              <p className="overline">{label(g.key, lang)}</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {(g.children || []).map((c) => (
                  <button
                    key={c.key}
                    onClick={() => { navigate(`/shop?category=${encodeURIComponent(c.key)}`); setMobileOpen(false); }}
                    className="text-left text-sm text-brand-sub"
                  >
                    {label(c.key, lang)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </header>
  );
};
